import React, { Component, Fragment } from 'react';

import Form from 'components/form/Form';
import FormControl from 'components/form/FormControl';
import InputGroup from 'components/input/group/InputGroup';
import InputToggle from 'components/input/toggle/InputToggle';
import { Loader } from 'components/loader';
import { I18n, translate } from 'components/translate';
import Settings from 'configs/Settings';
import { normalizeIgnoreCase } from 'helpers/String';
import flowRight from 'lodash/flowRight';
import get from 'lodash/get';
import { inject, observer, PropTypes as MobxPropTypes } from 'mobx-react';
import AuthService from 'modules/auth/AuthService';
import AnalyticsScriptDescription from 'modules/configManager/configData/analyticsScriptDescription';
import AnalyticsScriptV4Alert from 'modules/configManager/configData/analyticsScriptV4Alert';
import configDataUtil from 'modules/configManager/configData/configDataUtil';
import { NewCustomDomainAlert } from 'modules/configManager/configData/newCustomDomainAlert';
import ConfigService from 'modules/configManager/ConfigService';
import { ANALYTICS_SCRIPT_FORMAT_V4 } from 'modules/configManager/constants';
import { ContentBasePage } from 'modules/content';
import { FeatureToggles, FeatureToggleService } from 'modules/featureToggle';
import { UiService } from 'modules/ui';
import { UserStore } from 'modules/user';
import { PropTypes } from 'prop-types';

import ConfigManagerEvents from '../ConfigManagerEvents';

import './ConfigData.scss';

class ConfigData extends Component {
  inputSubdomainRef = React.createRef();
  inputDomainRef = React.createRef();

  static propTypes = {
    ConfigStore: MobxPropTypes.objectOrObservableObject,
    fixedBarId: PropTypes.string,
    tabIndex: PropTypes.number
  };

  state = {
    showLoader: true,
    shouldShowNewCustomDomainAlert: false
  };

  async componentDidMount() {
    ConfigService.fetchBasicData();
    this.toggleLoader();
    await this.loadOauthData();
    await this.checkToShowCustomDomainAlert();
  }

  async loadOauthData() {
    const refreshToken = Settings.refreshTokenCookie;

    if (refreshToken) {
      try {
        const oauthData = await AuthService.refreshRequest({
          refresh_token: refreshToken
        });

        Settings.updateOauthData(oauthData);
        Settings.updateCookiesIntegration(oauthData.access_token);

        try {
          const checkToken = await AuthService.checkRequest({
            access_token: oauthData.access_token
          });

          Settings.updateCheckTokenData(checkToken);
          // eslint-disable-next-line no-empty
        } catch {}
        // eslint-disable-next-line no-empty
      } catch {}
    }
  }

  toggleLoader = () => {
    this.setState({ showLoader: !this.state.showLoader });
  };

  async checkToShowCustomDomainAlert() {
    const userId = UserStore.marketplaceId;

    const isEnableHotmartClubAdminCustomDomain =
      await FeatureToggleService.getFeatureToggleStatus(
        FeatureToggles.EnableHotmartClubAdminCustomDomain,
        userId
      );

    const { domain } = this.props.ConfigStore.data;

    this.setState({
      shouldShowNewCustomDomainAlert:
        isEnableHotmartClubAdminCustomDomain && !domain
    });
  }

  async updateOauthData(oldSubdomain, newSubdomain, shouldSaveRefreshCookie) {
    const oauthData = await AuthService.refreshRequest({
      refresh_token: Settings.refreshToken
    });

    shouldSaveRefreshCookie &&
      Settings.saveRefreshTokenCookie(oauthData.refresh_token);
    Settings.updateCookiesIntegration(oauthData.access_token);
    AuthService.auth = oauthData;

    window.location.href = window.location.href.replace(
      oldSubdomain,
      newSubdomain
    );
  }

  saveHandler = async (data) => {
    this.toggleLoader();
    try {
      const { subdomain, domain } = data;
      const normalizedSubdomain = normalizeIgnoreCase(subdomain);

      const formatedData = {
        ...data,
        subdomain: normalizedSubdomain,
        domain: normalizeIgnoreCase(domain)
      };

      await ConfigService.updateBasicData(formatedData);

      if (normalizedSubdomain !== Settings.subdomain) {
        const shouldSaveRefreshCookie = true;

        this.updateOauthData(
          Settings.subdomain,
          normalizedSubdomain,
          shouldSaveRefreshCookie
        );
      }
    } finally {
      this.toggleLoader();
    }
  };

  copyToClipboard() {
    const input = this.inputSubdomainRef.current;

    if (!input) {
      return;
    }

    const domain = `${input.value}.club.hotmart.com`;

    input.value = domain;
    input.select();
    document.execCommand('copy');
    input.focus();

    UiService.setAlertSuccess('done');

    this.formNode.setField({
      name: 'submain',
      value: domain.replace('.club.hotmart.com', '')
    });
  }

  checkDomainAvailability() {
    const domain = this.inputDomainRef.current;

    if (domain.value) {
      ConfigService.checkDomainAvailability(domain.value);
    } else {
      domain.focus();
      UiService.setAlertError('config.data.emply_domain');
    }
  }

  handleChangeSignupAllowed = (event) => {
    const value = get(event, 'target.value', null);

    value !== null && ConfigManagerEvents.freeSignUpToggle(value);
  };

  render() {
    const {
      analyticsScript,
      description,
      hasReportEmailSendFeature,
      knowMoreUrl,
      name,
      published,
      reportEmailSendFeatureEnabled,
      signupAllowed,
      subdomain,
      domain
    } = this.props.ConfigStore.data;
    const { showLoader, shouldShowNewCustomDomainAlert } = this.state;
    const { fixedBarId, tabIndex, t } = this.props;

    return (
      <Fragment>
        {showLoader ? (
          <Loader key="loader" />
        ) : (
          <Form
            ref={(node) => (this.formNode = node)}
            onSubmit={this.saveHandler}
          >
            <div
              className="grid grid__block"
              data-test="settings-data-information"
            >
              <div className="card__description">
                <h2>
                  <I18n label="general.informations" />
                </h2>
                <p>
                  <I18n label="config_manager.config_data.info_description" />
                </p>
              </div>
              <FormControl>
                <InputGroup
                  required
                  name="name"
                  value={name}
                  maxLength="255"
                  autoComplete="off"
                  labelValue="config_manager.config_data.area_name"
                  placeholder="config_manager.config_data.area_name_placeholder"
                  data-test="setting-data-area-name"
                />
              </FormControl>
              <FormControl>
                <InputGroup
                  name="description"
                  value={description}
                  description="config_manager.config_data.area_description_info"
                  maxLength="255"
                  autoComplete="off"
                  labelValue="config_manager.config_data.area_description"
                  placeholder="config_manager.config_data.area_description_placeholder"
                  data-test="setting-data-area-description"
                />
              </FormControl>
              <FormControl>
                <InputGroup
                  name="knowMoreUrl"
                  value={knowMoreUrl}
                  type="url"
                  maxLength="255"
                  className="mb__0"
                  autoComplete="off"
                  description="config_manager.config_data.area_know_course_info"
                  labelValue="config_manager.config_data.area_know_course"
                  placeholder="config_manager.config_data.area_know_course_placeholder"
                  data-test="setting-data-area-link"
                />
              </FormControl>
            </div>

            <div
              className="grid grid__block"
              data-test="settings-data-host-mapping"
            >
              <div className="card__description mb__sm">
                <h2>
                  <I18n label="config_manager.config_data.host" />
                </h2>
                <p>
                  <I18n label="config_manager.config_data.host_description" />
                </p>
              </div>
              <div className="host_subdomain_wrapper mb__md">
                <FormControl>
                  <InputGroup
                    required
                    inputClassName="input--lowercase-only"
                    name="subdomain"
                    value={subdomain}
                    ref={this.inputSubdomainRef}
                    maxLength="50"
                    minLength="5"
                    autoComplete="off"
                    labelValue="config_manager.config_data.host_subdomain"
                    placeholder="config_manager.config_data.host_subdomain_placeholder"
                  >
                    <p className="secondary mb__0">{'.club.hotmart.com'}</p>
                  </InputGroup>
                </FormControl>
                <button
                  id="copyToClipboard"
                  className="hot-button hot-button--secondary hot-button--lg"
                  onClick={() => this.copyToClipboard()}
                >
                  <I18n label="config_manager.config_data.host_subdomain_copy" />
                </button>
              </div>

              {shouldShowNewCustomDomainAlert ? (
                <NewCustomDomainAlert />
              ) : (
                <Fragment>
                  <FormControl>
                    <InputGroup
                      name="domain"
                      value={domain}
                      inputClassName="input--lowercase-only"
                      ref={this.inputDomainRef}
                      type="url"
                      maxLength="100"
                      autoComplete="off"
                      labelValue="config_manager.config_data.host_domain"
                      description="config_manager.config_data.host_domain_description"
                      placeholder="config_manager.config_data.host_domain_placeholder"
                    />
                  </FormControl>
                  <button
                    id="checkDomainAvailability"
                    className="hot-button hot-button--secondary hot-button--lg"
                    onClick={() => this.checkDomainAvailability()}
                  >
                    <I18n label="config_manager.config_data.host_domain_test" />
                  </button>
                </Fragment>
              )}
            </div>

            <div className="grid grid__block" data-test="settings-data-configs">
              <h2 className="_mb-6">
                <I18n label="config_manager.title" />
              </h2>
              <FormControl>
                <InputGroup
                  name="analyticsScript"
                  value={analyticsScript}
                  maxLength="17"
                  minLength="9"
                  autoComplete="off"
                  labelValue={t(
                    'config_manager.config_data.analytics_integration_v2.label'
                  )}
                  description={<AnalyticsScriptDescription />}
                  placeholder={t(
                    'config_manager.config_data.analytics_integration_v2.placeholder',
                    { data: { format: ANALYTICS_SCRIPT_FORMAT_V4 } }
                  )}
                  translate={false}
                />
              </FormControl>
              {analyticsScript &&
                configDataUtil.analyticsScriptIsV1(analyticsScript) && (
                  <AnalyticsScriptV4Alert />
                )}
              <label className="_mt-6">
                <I18n label="config_manager.config_data.access_control" />
              </label>
              <FormControl>
                <InputToggle
                  name="published"
                  value={published}
                  labelValue="config_manager.config_data.publish_area"
                  labelClass="secondary"
                />
              </FormControl>
              <FormControl>
                <InputToggle
                  name="signupAllowed"
                  value={signupAllowed}
                  labelValue="config_manager.config_data.enable_free_signup"
                  labelTooltip="config_manager.config_data.enable_free_signup_tooltip"
                  labelClass="secondary"
                  position="center"
                  rerenderPosition={tabIndex === 0}
                  onChange={this.handleChangeSignupAllowed}
                  data-test="signup-allowed"
                />
              </FormControl>
              {hasReportEmailSendFeature && (
                <Fragment>
                  <label>
                    <I18n label="notification.title" />
                  </label>
                  <FormControl>
                    <InputToggle
                      className="mb__0"
                      name="reportEmailSendFeatureEnabled"
                      value={reportEmailSendFeatureEnabled}
                      labelValue="config_manager.config_data.allow_emails_users"
                      labelTooltip="config_manager.config_data.allow_emails_users_tooltip"
                      labelClass="secondary"
                      position="center"
                      rerenderPosition={tabIndex === 0}
                    />
                  </FormControl>
                </Fragment>
              )}
            </div>
            {fixedBarId && <ContentBasePage.Footer onlySave />}
          </Form>
        )}
      </Fragment>
    );
  }
}

export default flowRight(
  translate(),
  inject('ConfigStore'),
  observer
)(ConfigData);

export { ConfigData };
